import React, { useEffect, useRef, useState } from 'react'
import styles from './ChatMessageList.module.scss'
import ChatMessage from '../ChatMessage/ChatMessage'
import { ReactComponent as Paper } from 'components/CRM/UI/icons/Paper.svg'
import ActionMenu from 'components/UI/ActionMenu/ActionMenu'
import swal from 'sweetalert'
import { superAxiosRequest } from 'axiosApi'
import asideModalStore from 'Stores/asideModalStore'
import EditNote from 'components/ContentBox/Global/GlobalModalSingle/templates/CRM/EditNote/EditNote'
import useLocalization from 'hooks/useLocalization'

const ChatMessageList = ({
  messages,
  notes,
  setMessages,
  dealData,
  messageStatus,
  newMessageId,
  dealId,
  setDealData,
  fetchDealData,
  fetchMessages,
  fetchNotes,
  pinPage,
  messageRefs,
  setPinPage,
  shouldScroll,
  setShouldScroll,
  searchTerm,
  funnelId,
  editTask,
  sessionId,
  channelId,
}) => {
  const containerRef = useRef(null)

  // Состояние для отслеживания раскрытой заметки (хранит ID или null)
  const [expandedNoteId, setExpandedNoteId] = useState(null)

  const { translate } = useLocalization({
    section: 'crm',
    subsection: 'customer_card',
    dict: {
      edit: 'Edit',
      delete: 'Delete',
      are_you_sure: 'Are you sure',
      confirm_note_deletion: 'Are you sure you want to delete the note',
      no: 'No',
      yes: 'Yes',
      edit_note: 'Edit Note',
      save: 'Save',
      today: 'Today',
      yesterday: 'Yesterday',
    },
  })

  const noteMenu = [
    { name: translate.edit, action: 'edit' },
    { name: translate.delete, action: 'delete' },
  ]

  useEffect(() => {
    if (!pinPage && shouldScroll) {
      scrollToBottom()
    }
  }, [messages, notes, pinPage, shouldScroll])

  useEffect(() => {
    const hasPinnedMessages = messages.some((el) => el?.data?.is_pinned)
    if (!hasPinnedMessages) {
      setPinPage(false)
      setShouldScroll(true)
    }
  }, [messages])

  const scrollToBottom = () => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight
    }

    const lastMessageId = messages[messages.length - 1]?.data?.id
    if (lastMessageId && messageRefs.current[lastMessageId]) {
      messageRefs?.current[lastMessageId]?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }

  const deleteNote = (noteId) => {
    swal({
      title: `${translate.are_you_sure}?`,
      text: `${translate.confirm_note_deletion}?`,
      icon: 'warning',
      buttons: [translate.no, translate.yes],
      dangerMode: true,
    }).then((yes) => {
      if (yes) {
        superAxiosRequest({
          method: 'delete',
          link: `integration/deal_notes/${noteId}/`,
          crm: true,
        })
          .then(() => {
            if (fetchNotes) fetchNotes(dealId)
          })
          .catch((err) => {
            console.error('Ошибка при удалении заметки:', err)
          })
      }
    })
  }

  const editNote = (noteId, currentText) => {
    const ms = {
      modalBodyComponent: () => <EditNote />,
      button: translate.save,
      link: `integration/deal_notes/${noteId}/`,
      title: translate.edit_note,
      crm: true,
      is_put: true,
      isResponsive: true,
      onBeforeSubmit: (formData) => {
        return {
          text: formData.note || formData.text,
          deal_id: dealId,
        }
      },
      onSubmitSuccess: () => {
        if (fetchNotes) fetchNotes(dealId)
        asideModalStore.close()
      },
      valid: {
        note: 'required',
      },
    }

    const md = {
      note: currentText,
    }

    asideModalStore.activity(ms, md)
  }

  const toggleNoteExpand = (noteId) => {
    setExpandedNoteId((prevId) => (prevId === noteId ? null : noteId))
  }

  const noteAction = (action, noteId, noteText, e) => {
    if (e) e.stopPropagation()

    switch (action) {
      case 'edit':
        editNote(noteId, noteText)
        break
      case 'delete':
        deleteNote(noteId)
        break
      default:
        break
    }
  }

  const renderMessagesWithSeparators = () => {
    let currentDate = null

    return messages?.map((message, index) => {
      const messageDate = new Date(
        message?.data?.sent_at ||
          message?.data?.started_at ||
          message?.data?.created_at,
      )

      const showSeparator =
        !currentDate ||
        messageDate.toDateString() !== currentDate.toDateString()
      currentDate = messageDate

      return (
        <React.Fragment key={`msg-${message.data?.id}-${index}`}>
          {showSeparator && (
            <div className={styles.separator}>
              <div />
              {formatDate(messageDate)}
              <div />
            </div>
          )}
          <ChatMessage
            key={`chat-msg-${message.data?.id}-${index}`}
            ref={(ref) => {
              if (message?.data?.id) {
                messageRefs.current[message.data.id] = ref
              }
            }}
            setShouldScroll={setShouldScroll}
            type={message.type}
            pinPage={pinPage}
            setMessages={setMessages}
            fetchMessages={fetchMessages}
            messages={messages}
            message={message.data}
            dealData={dealData}
            messageStatus={messageStatus}
            newMessageId={newMessageId}
            isHighlighted={message.data?.id === newMessageId}
            error={message.data?.sending_status === 3}
            searchTerm={searchTerm}
            funnelId={funnelId}
            editTask={editTask}
            dealId={dealId}
            sessionId={sessionId}
            channelId={channelId}
          />
        </React.Fragment>
      )
    })
  }

  const formatDate = (date) => {
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    if (date.toDateString() === today.toDateString()) {
      return translate.today
    } else if (date.toDateString() === yesterday.toDateString()) {
      return translate.yesterday
    } else {
      const options = { day: '2-digit', month: '2-digit', year: 'numeric' }
      return date.toLocaleDateString(undefined, options)
    }
  }

  const parseLinks = (text) => {
    if (!text) return text
    const urlRegex =
      /((https?:\/\/|www\.)[^\s]+)|([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g
    const matches = [...text.matchAll(urlRegex)]
    if (matches.length === 0) return text

    const result = []
    let lastIndex = 0

    matches.forEach((match, index) => {
      const fullMatch = match[0]
      const matchIndex = match.index

      if (matchIndex > lastIndex) {
        result.push(text.substring(lastIndex, matchIndex))
      }

      let href = fullMatch
      let target = '_blank'
      let rel = 'noopener noreferrer'
      let linkClass = styles.link

      if (fullMatch.startsWith('www.')) {
        href = `https://${fullMatch}`
      } else if (fullMatch.includes('@') && !fullMatch.startsWith('mailto:')) {
        href = `mailto:${fullMatch}`
        target = undefined
        rel = undefined
        linkClass = styles.emailLink
      }

      result.push(
        <a
          key={`link-${index}`}
          href={href}
          target={target}
          rel={rel}
          className={linkClass}
        >
          {fullMatch}
        </a>,
      )
      lastIndex = matchIndex + fullMatch.length
    })

    if (lastIndex < text.length) {
      result.push(text.substring(lastIndex))
    }
    return result
  }

  return (
    <div className={styles.container}>
      <div className={styles.content} ref={containerRef}>
        {notes && notes.length > 0 && (
          <div className={styles.notesStickyWrapper}>
            {notes.map((noteItem) => {
              const noteId = noteItem.id || noteItem.data?.id
              const noteText =
                noteItem.text ||
                noteItem.data?.text ||
                noteItem.note ||
                noteItem.data?.note
              const noteDate = noteItem.created_at || noteItem.data?.created_at

              if (!noteText) return null

              const isExpanded = expandedNoteId === noteId
              const MAX_CHARS = 120
              const isLongText = noteText.length > MAX_CHARS

              const displayText =
                isExpanded || !isLongText
                  ? noteText
                  : `${noteText.substring(0, MAX_CHARS)}...`

              return (
                <div
                  key={`note-${noteId}`}
                  className={styles.noteBlock}
                  onClick={() => toggleNoteExpand(noteId)}
                >
                  <Paper className={styles.noteIcon} />
                  <div className={styles.note}>
                    <div className={styles.noteContent}>
                      {noteDate && (
                        <div className={styles.noteDate}>
                          {new Date(noteDate).toLocaleString('ru-RU', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                      )}
                      <div
                        className={
                          isExpanded
                            ? styles.textExpanded
                            : styles.textCollapsed
                        }
                      >
                        {parseLinks(displayText)}
                      </div>
                    </div>

                    <div onClick={(e) => e.stopPropagation()}>
                      <ActionMenu
                        menu={noteMenu}
                        action={(action) =>
                          noteAction(action, noteId, noteText)
                        }
                      />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {renderMessagesWithSeparators()}
      </div>
    </div>
  )
}

export default ChatMessageList
